import { appAliases, appOrder } from "@openanalyst-ai/core/app-registry"
export type RuntimeAppWorkerStatus = {
  appId: string
  state: "blocked" | "starting" | "ready" | "restarting" | "unhealthy" | "crashed" | "stopping" | "stopped"
  reason?: string
  pid?: number
  startedAt?: number
  readyAt?: number
  restartCount: number
}

export type RuntimePluginApp = {
  id: string
  title: string
  description?: string
  icon: string
  route: string
  viewToolName: string
  capabilities: string[]
  worker?: RuntimeAppWorkerStatus
  templateProfile?: "system" | "public"
  home?: {
    title?: string
    description?: string
    menu?: string[]
    banner?: {
      items?: Array<{
        type?: "image" | "video" | "youtube" | "embed"
        src?: string
        url?: string
        poster?: string
        alt?: string
        title?: string
      }>
      images?: string[]
      intervalMs?: number
      fit?: "cover" | "contain"
    }
    about?: {
      title?: string
      description?: string
      tools?: Array<string | { label: string; description?: string }>
    }
    tools?: Array<string | { label: string; description?: string }>
    contact?: {
      label?: string
      email?: string
      description?: string
    }
  }
  // Top-level UI features (S1.5) — drives the L1 feature switcher. Optional:
  // when absent/empty the shell infers account+canvas for back-compat.
  features?: Array<{
    id: string
    label: string
    route?: string
    enabled?: boolean
    icon?: string
    kind: "native" | "artifact"
    listTool?: string
    detailTool?: string
    display?: "cards" | "list" | "table" | "calendar"
    titleField?: string
    imageField?: string
    descField?: string
  }>
  appContract?: {
    version?: string
    role?: "major_app" | "plugin" | "platform"
    intent?: string
    shell?: {
      titleLocked?: boolean
      sidebarLocked?: boolean
      footerLocked?: boolean
      zones?: string[]
    }
    sockets?: Array<{
      id: string
      label: string
      capability?: string
      status?: "available" | "missing" | "connected" | "warning" | "error"
      required?: boolean
      provider?: string
      routeFamily?: string
      description?: string
    }>
    plugs?: Array<{
      id: string
      label: string
      appId?: string
      capability?: string
      status?: "available" | "missing" | "connected" | "warning" | "error"
      required?: boolean
      description?: string
      actions?: string[]
    }>
    orchestrates?: Array<{
      pluginId: string
      label: string
      capability?: string
      required?: boolean
      description?: string
    }>
  }
}

export function pluginAppReadiness(app: RuntimePluginApp) {
  if (!app.worker) return { ready: true as const }
  if (app.worker.state === "ready") return { ready: true as const }
  return {
    ready: false as const,
    state: app.worker.state,
    reason: app.worker.reason ?? `worker_${app.worker.state}`,
  }
}

export function isOpenAnalystWorkspaceID(value?: string | null) {
  return value?.trim().startsWith("wrk_") ?? false
}
export const pluginToolPrefix = (id: string) => id.replace(/[^a-zA-Z0-9_]/g, "_")

// The alias map is DERIVED from the single app registry — it used to be a hand-kept
// SECOND copy of the engine's map, and it had drifted: every design-studio and
// oa-computer alias was missing here, so those ids failed to canonicalize in the UI
// while working fine in the engine. That was a live bug. One source now.
const PLUGIN_APP_ID_ALIASES: Record<string, string> = appAliases()

export function canonicalPluginAppID(id: string) {
  const normalized = id.trim().toLowerCase()
  return PLUGIN_APP_ID_ALIASES[normalized] ?? normalized
}

const PLUGIN_APP_ROUTE_QUERY_KEYS = new Set(["pluginApp", "pluginPopout", "popout"])

function mergePluginAppRouteSearch(query: URLSearchParams, search?: string | URLSearchParams) {
  if (!search) return query
  const source = typeof search === "string" ? new URLSearchParams(search.replace(/^\?/, "")) : search
  source.forEach((value, key) => {
    if (!PLUGIN_APP_ROUTE_QUERY_KEYS.has(key)) query.set(key, value)
  })
  return query
}

export function pluginAppSessionHref(input: {
  dir: string
  id: string
  sessionID?: string
  popout?: boolean
  openAnalystWorkspaceId?: string | null
  search?: string | URLSearchParams
}) {
  const id = canonicalPluginAppID(input.id)
  const query = new URLSearchParams(input.popout ? { popout: "1", pluginPopout: id } : { pluginApp: id })
  if (isOpenAnalystWorkspaceID(input.openAnalystWorkspaceId)) query.set("workspace", input.openAnalystWorkspaceId!.trim())
  mergePluginAppRouteSearch(query, input.search)
  return `/${input.dir}/session${input.sessionID ? `/${input.sessionID}` : ""}?${query.toString()}`
}

export function publishPluginApp(apps: RuntimePluginApp[]) {
  return apps.find(
    (app) =>
      canonicalPluginAppID(app.id) === "10x-publish" ||
      app.appContract?.plugs?.some((plug) => plug.capability === "10x:publish"),
  )
}

export function publishPluginIDForTool(toolName: string, apps: RuntimePluginApp[]) {
  const prefix = toolName.split(".")[0]
  return apps.find((app) => pluginToolPrefix(app.id) === prefix)?.id ?? publishPluginApp(apps)?.id ?? "10x-publish"
}

// Fixed left-sidebar order: dashboard -> memory -> crm -> generative -> 10x publish,
// then everything else alphabetically by title. Pinned plugins lead; unknown ids
// fall into the alpha-sorted tail.
function pluginRank(id: string) {
  return appOrder(id)
}

export function pluginLinks(input: { dir: string; apps: RuntimePluginApp[]; openAnalystWorkspaceId?: string | null }) {
  const seen = new Set<string>()
  return input.apps
    .toReversed()
    .filter((app) => {
      const id = canonicalPluginAppID(app.id)
      if (seen.has(id)) return false
      seen.add(id)
      return true
    })
    .toReversed()
    .map((app) => {
      const id = canonicalPluginAppID(app.id)
      return {
        id,
        title: app.title,
        icon: app.icon,
        capabilities: app.capabilities,
        href: pluginAppSessionHref({
          dir: input.dir,
          id,
          openAnalystWorkspaceId: input.openAnalystWorkspaceId,
        }),
      }
    })
    .sort((a, b) => {
      const rank = pluginRank(a.id) - pluginRank(b.id)
      return rank !== 0 ? rank : (a.title ?? "").localeCompare(b.title ?? "")
    })
}
